import { useEffect, useState } from "react";
import { History, MessageSquareText, Target, Workflow } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import QuestionSection from "./QuestionSection";
import  { MappingTable } from "../mapping/MappingCard";
import { Button } from "@/components/ui/Button";
import Select from "@/components/ui/Select";
import { COMPETENCY_MAPPING_TYPES, SENIORITY_REQUIRED_TYPES } from "../../constants/interviewTypes";
import VerticalSectionNav from "../../../component/VerticalSectionNav";

const TYPE_META = {
  SCREENING: { title: "Screening Questions", description: "Short 15–30 min recruiter call questions" },
  EXPERIENCE_BASED: { title: "Experience-Based Questions", description: "Competency-based questions for structured interviews" },
  DOMAIN_SPECIFIC: { title: "Domain Specific Questions", description: "Role-specific technical and domain questions" },
  SITUATIONAL_CASE: { title: "Situational & Case Questions", description: "Scenario-based and problem-solving questions" },
  MOTIVATIONAL: { title: "Motivational Questions", description: "Career aspiration and culture-fit questions" },
  LEADERSHIP: { title: "Leadership Questions", description: "Senior-level strategic and leadership questions" },
};

const COMP_TABS = [
  { id: "questions", label: "Questions", icon: MessageSquareText },
  { id: "competencies", label: "Competencies", icon: Target },
  { id: "mapping", label: "Mapping", icon: Workflow },
];

const QuestionsUI = ({ questionTypes = {}, configuredTypes = [],allTypeEntries = [], onRegenerate, seniorityLevels = [] ,canGenerateQuestions}) => {
const [activeType, setActiveType] = useState(null);
  const [typeLevels, setTypeLevels] = useState({});
  const [compTabs, setCompTabs] = useState({});

  const setTypeLevel = (type, level) => {
    setTypeLevels((prev) => ({ ...prev, [type]: level }));
  };

  const getPriorityColorIndex = (priority) => {
    if (priority <= 2) return 0;
    if (priority <= 4) return 1;
    return 2;
  };

const interviewSections = allTypeEntries
  .filter((ct) => {
    const typeData = questionTypes[ct.value] || {};

    return (
      ct.isActive ||
      typeData.hasQuestions ||
      typeData.questions?.length > 0 ||
      typeData.regenProgress
    );
  })
  .map((ct) => ({
    id: ct.value,
    label: ct.label,
    count: questionTypes?.[ct.value]?.questions?.length || 0,
    isActive: ct.isActive,
    disabled: !ct.isActive,
  }));

 const currentActiveType =
  activeType || interviewSections[0]?.id;

useEffect(() => {
  if (!interviewSections.length) return;

  setActiveType((prev) => {    
    // preserve selected tab if it still exists
    if (
      prev &&
      interviewSections.some((s) => s.id === prev)
    ) {
      return prev;
    }

    // otherwise first rendered tab
    return interviewSections[0].id;
  });
}, [interviewSections]);

return (
<div className="flex h-full min-h-0 w-full">
    <div className="pt-4 pb-5 pl-4 min-h-0 border-r border-border-primary overflow-x-hidden overflow-y-auto custom-scrollbar">
      <VerticalSectionNav
        sections={interviewSections}
        activeSection={activeType}
        onSelect={setActiveType}
      />
    </div>

    <div className="flex-1 min-w-0 min-h-0 overflow-hidden flex flex-col">
      {allTypeEntries
        .filter((ct) => ct.value === currentActiveType)
        .map((ct) => {
        const typeData = questionTypes[ct.value] || {
          questions: [],
          hasQuestions: false,
          regenProgress: null,
          isActive: true,
        };

        const isActive = ct.isActive !== false;

        const meta = TYPE_META[ct.value] || {
          title: `${ct.label} Questions`,
          description: "",
        };

        const currentLevel = typeLevels[ct.value] || "MID";

        const showCompMapping =
          COMPETENCY_MAPPING_TYPES.includes(ct.value);

        const needsSeniority =
          SENIORITY_REQUIRED_TYPES.includes(ct.value);

        const competencies = typeData.competencies || [];
        const mappings = typeData.mappings || [];

        const hasCompData =
          competencies.length > 0 || mappings.length > 0;

        const activeCompTab =
          compTabs[currentActiveType] || "questions";

const questionSectionProps = {
  type: ct.value,
  title: meta.title,
  questions: typeData.questions,
  hasQuestions: typeData.hasQuestions,
  regenProgress: typeData.regenProgress,
  onRegenerate: () => onRegenerate(ct.value, typeData.hasQuestions, currentLevel),
  needsSeniority,
  seniorityLevels,
  currentLevel,
  onLevelChange: (level) => setTypeLevel(ct.value, level),
  isActive,
  canGenerateQuestions,
};

return (
  <div
    key={ct.value}
    className="flex flex-col flex-1 min-h-0 border-none px-5 pt-4"
  >
    <div className="flex-1 min-h-0 flex flex-col overflow-y-auto custom-scrollbar pr-2">
      {showCompMapping && hasCompData ? (
        <>
          <div className="sticky top-0 z-20 border-b border-border-primary mb-4 bg-background-secondary pb-0.5">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                {COMP_TABS.map((tab) => {
                  const count =
                    tab.id === "questions"
                      ? typeData.questions.length
                      : tab.id === "competencies"
                      ? competencies.length
                      : mappings.length;

                  return (
                    <Button
                      key={tab.id}
                      onClick={() =>
                        setCompTabs((prev) => ({ ...prev, [ct.value]: tab.id }))
                      }
                      className={`relative flex items-center gap-1.5 px-3 pb-2 text-xs font-medium ${
                        activeCompTab === tab.id ? "text-text-primary" : "text-text-secondary"
                      }`}
                    >
                      <tab.icon size={14} />
                      {tab.label}
                      {count > 0 && (
                        <span
                          className={`text-[10px] font-semibold px-1 rounded-full border-2 ${
                            activeCompTab === tab.id
                              ? "bg-background-invert text-text-invert"
                              : "border-border-primary text-text-secondary"
                          }`}
                        >
                          {String(count).padStart(2, "0")}
                        </span>
                      )}
                      {activeCompTab === tab.id && (
                        <div className="absolute bottom-0 left-0 w-full h-[2px] bg-background-invert" />
                      )}
                    </Button>
                  );
                })}
              </div>

              <div className="flex items-center gap-3 shrink-0 pb-1">
                {!isActive && (
                  <span className="text-[10px] font-medium px-2 py-1.5 rounded-full bg-tone-rose-soft text-tone-rose border border-tone-rose tracking-wide">
                    Disabled
                  </span>
                )}
                {typeData.hasQuestions && isActive && canGenerateQuestions && (
                  <>
                    {needsSeniority && seniorityLevels.length > 0 && (
                      <Select
                        value={currentLevel}
                        onChange={(value) => setTypeLevel(ct.value, value)}
                        options={seniorityLevels}
                        className="h-8 rounded-lg px-3 py-1.5 text-xs"
                      />
                    )}
                    <Button
                      onClick={() => onRegenerate(ct.value, typeData.hasQuestions, currentLevel)}
                      disabled={!!typeData.regenProgress}
                      className="bg-background-tertiary hover:bg-button-secondary hover:text-text-base text-text-primary px-3 py-1.5 flex items-center gap-1.5 rounded-full text-xs"
                    >
                      <History size={12} className={typeData.regenProgress ? "animate-spin" : ""} />
                      {typeData.regenProgress ? "Generating..." : "Regenerate"}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="pr-2 pb-4">
            {activeCompTab === "questions" && (
              <QuestionSection {...questionSectionProps} />
            )}
            {activeCompTab === "competencies" && competencies.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                  <Target size={15} />
                  Extracted Competencies
                </h4>
                <div className="flex flex-wrap gap-2">
                  {competencies.map((comp, i) => (
                    <Badge
                      key={comp.id || comp.name || i}
                      label={comp.name}
                      variant="gradient"
                      colorIndex={getPriorityColorIndex(comp.priority)}
                      className="!rounded-md !text-text-primary"
                      badgeStyle="!py-1 text-[10px] md:text-sm"
                    />
                  ))}
                </div>
              </div>
            )}
            {activeCompTab === "mapping" && mappings.length > 0 && (
              <MappingTable mappings={mappings} />
            )}
          </div>
        </>
      ) : (
        <>
          {/* Top-right controls when no comp tabs */}
          <div className="shrink-0 flex justify-end gap-3 mb-4 sticky top-0 z-20 bg-background-secondary pb-0.5">
            {!isActive && (
              <span className="text-[10px] font-medium px-2 py-1.5 rounded-full bg-tone-rose-soft text-tone-rose border border-tone-rose tracking-wide">
                Disabled
              </span>
            )}
            {typeData.hasQuestions && isActive && canGenerateQuestions && (
              <>
                {needsSeniority && seniorityLevels.length > 0 && (
                  <Select
                    value={currentLevel}
                    onChange={(value) => setTypeLevel(ct.value, value)}
                    options={seniorityLevels}
                    className="h-8 rounded-lg px-3 py-1.5 text-xs"
                  />
                )}
                <Button
                  onClick={() => onRegenerate(ct.value, typeData.hasQuestions, currentLevel)}
                  disabled={!!typeData.regenProgress}
                  className="bg-background-tertiary hover:bg-button-secondary hover:text-text-base text-text-primary px-3 py-1.5 flex items-center gap-1.5 rounded-full text-xs"
                >
                  <History size={12} className={typeData.regenProgress ? "animate-spin" : ""} />
                  {typeData.regenProgress ? "Generating..." : "Regenerate"}
                </Button>
              </>
            )}
          </div>
          <div className="pr-2 pb-4">
            <QuestionSection {...questionSectionProps} />
          </div>
        </>
      )}
    </div>
  </div>
);
      })}
    </div>
</div>
);
};

export default QuestionsUI;